import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

function usePrev(value) {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

function useOnClickOutside(ref, handler) {
  useEffect(
    () => {
      const listener = (event) => {
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }

        handler(event);
      };

      document.addEventListener('mousedown', listener);
      document.addEventListener('touchstart', listener);

      return () => {
        document.removeEventListener('mousedown', listener);
        document.removeEventListener('touchstart', listener);
      };
    },
    [ref, handler],
  );
}

function useLocalStorage(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (err) {
      return initialValue;
    }
  });

  const updateValue = useCallback((val) => {
    setValue(val);
    window.localStorage.setItem(key, JSON.stringify(val));
  }, [key]);

  return [value, updateValue];
}

function useResizeObserver() {
  const [size, setSize] = useState({ width: 0, height: 0 });

  const elementRef = useRef(null);

  useEffect(() => {
    const observedElement = elementRef.current;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });

    if (observedElement) {
      observer.observe(observedElement);
    }

    return () => {
      if (observedElement) {
        observer.unobserve(observedElement);
      }
    };
  }, []);

  return { size, elementRef };
}

function useDebounce(fn, delay) {
  const timeoutRef = useRef();

  const debouncedFn = (...args) => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => fn(...args), delay);
  };

  return debouncedFn;
}

const useThrottle = (callback, delay) => {
  const lastCall = useRef(0);

  return useCallback((...args) => {
    const now = Date.now();
    if (now - lastCall.current >= delay) {
      lastCall.current = now;
      callback(...args);
    }
  }, [callback, delay]);
};

function useLoginModal() {
  const [searchParams, setSearchParams] = useSearchParams();

  function setShowLoginModal(type) {
    if (!type) {
      searchParams.delete('showLoginModal');
      searchParams.delete('accountConfirmed');
      searchParams.delete('loginType');
      searchParams.delete('forgotPassword');
      searchParams.delete('code');
      searchParams.delete('email');
    } else {
      searchParams.set('showLoginModal', true);
      searchParams.set('loginType', type);
    }

    setSearchParams(searchParams, { replace: true });
  }

  const value = searchParams.get('showLoginModal');

  return {
    showLoginModal: value === 'false' ? false : value,
    setShowLoginModal,
    loginType: searchParams.get('loginType'),
    accountConfirmed: searchParams.get('accountConfirmed'),
    resetPassword: searchParams.get('forgotPassword'),
    closeResetPassword: () => {
      searchParams.delete('forgotPassword');
      searchParams.delete('code');
      searchParams.delete('email');
      setSearchParams(searchParams, { replace: true });
    },
    code: searchParams.get('code'),
    email: searchParams.get('email'),
  };
}

function useMapBounds(map) {
  const [mapBounds, setMapBounds] = useState(undefined);

  useEffect(() => {
    function updateBounds() {
      if (map) {
        const bounds = map.getBounds();
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();

        setMapBounds({
          minLat: sw.lat,
          minLng: sw.lng,
          maxLat: ne.lat,
          maxLng: ne.lng,
        });
      }
    }

    updateBounds();

    map?.on('moveend', updateBounds);
    map?.on('resize', updateBounds);

    return () => {
      map?.off('moveend', updateBounds);
      map?.off('resize', updateBounds);
    };
  }, [map]);

  return mapBounds;
}

function useMapZoom(map) {
  const [mapZoom, setMapZoom] = useState(undefined);

  useEffect(() => {
    function updateZoom() {
      setMapZoom(map?.getZoom());
    }

    updateZoom();
    map?.on('zoomend', updateZoom);

    return () => map?.off('zoomend', updateZoom);
  }, [map]);

  return mapZoom;
}

function useScrollAtBottom(scrollParent, offset = 0) {
  const [isAtBottom, setIsAtBottom] = useState(false);

  useEffect(() => {
    const el = typeof scrollParent === 'function'
      ? scrollParent()
      : scrollParent;

    if (!el) return;

    let ticking = false;

    function handleScroll() {
      if (!ticking) {
        requestAnimationFrame(() => {
          const { scrollTop, clientHeight, scrollHeight } = el;
          
          // Add a larger threshold for mobile (typically 1-5px is good)
          const threshold = offset + 5;
          const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
          
          setIsAtBottom(distanceFromBottom <= threshold);
          ticking = false;
        });
        ticking = true;
      }
    }

    // Use passive listeners for better mobile performance
    el.addEventListener('scroll', handleScroll, { passive: true });
    
    // Also listen for resize events (important for mobile viewport changes)
    window.addEventListener('resize', handleScroll, { passive: true });
    
    // Initial check
    handleScroll();

    // eslint-disable-next-line consistent-return
    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [scrollParent, offset]);

  return isAtBottom;
}

export {
  useDebounce,
  useLocalStorage,
  useLoginModal,
  useMapBounds,
  useMapZoom,
  useOnClickOutside,
  usePrev,
  useResizeObserver,
  useThrottle,
  useScrollAtBottom,
}